import React, { useState } from "react";
import { motion } from "framer-motion";
import { PaperPlaneRight, CheckCircle, Phone, WhatsappLogo } from "@phosphor-icons/react";
import { useLang } from "../../context/LanguageContext";
import { api } from "../../lib/api";
import { Reveal, Kicker } from "./Reveal";

const empty = { name: "", phone: "", service: "", pickup: "", dropoff: "", date: "", time: "", passengers: "1", notes: "" };

export const Booking = ({ content }) => {
  const { t, lang, pick } = useLang();
  const [form, setForm] = useState(empty);
  const [status, setStatus] = useState("idle");
  const services = content?.services || [];
  const phone = content?.contact?.phone1 || "";
  const wa = content?.contact?.whatsapp || "";

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      await api.post("/bookings", form);
      setStatus("success");
      setForm(empty);
      setTimeout(() => setStatus("idle"), 6000);
    } catch (err) {
      setStatus("error");
      setTimeout(() => setStatus("idle"), 5000);
    }
  };

  const inputCls = "w-full bg-ink border border-white/15 text-white px-4 py-3.5 rounded-none outline-none focus:border-taxi focus:ring-1 focus:ring-taxi placeholder:text-white/30 transition-colors";
  const labelCls = "block text-white/60 text-xs font-bold uppercase tracking-widest mb-2";

  return (
    <section id="book" className="relative bg-taxi py-24 md:py-36" data-testid="booking-section">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-5">
            <Reveal><Kicker>{t("book_kicker")}</Kicker></Reveal>
            <Reveal delay={0.05}>
              <h2 className="font-display text-black text-5xl md:text-7xl uppercase leading-[0.9]">{t("book_title")}</h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-6 text-black/70 text-base md:text-lg leading-relaxed max-w-md">{t("book_desc")}</p>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="mt-10 border-t border-black/20 pt-8">
                <p className="text-black/70 text-sm mb-5">{t("or_call")}</p>
                <div className="flex flex-col gap-3">
                  <a href={`tel:${phone.replace(/[^0-9+]/g, "")}`} className="flex items-center gap-3 text-black hover:text-usared transition-colors font-bold text-lg" data-testid="booking-call-link">
                    <Phone size={20} weight="fill" className="text-black" /> {phone}
                  </a>
                  <a href={`https://wa.me/${wa}`} target="_blank" rel="noreferrer" className="flex items-center gap-3 text-black hover:text-[#128C7E] transition-colors font-bold text-lg" data-testid="booking-whatsapp-link">
                    <WhatsappLogo size={22} weight="fill" className="text-[#128C7E]" /> WhatsApp
                  </a>
                </div>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <Reveal delay={0.1}>
              <form onSubmit={submit} className="border border-white/10 bg-surface p-6 md:p-10" data-testid="booking-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label className={labelCls}>{t("f_name")}</label>
                    <input required value={form.name} onChange={set("name")} className={inputCls} data-testid="booking-name" placeholder="John Doe" />
                  </div>
                  <div>
                    <label className={labelCls}>{t("f_phone")}</label>
                    <input required value={form.phone} onChange={set("phone")} className={inputCls} data-testid="booking-phone" placeholder="+1 ..." />
                  </div>
                  <div className="md:col-span-2">
                    <label className={labelCls}>{t("f_service")}</label>
                    <select required value={form.service} onChange={set("service")} className={inputCls} data-testid="booking-service">
                      <option value="">—</option>
                      {services.map((s) => (
                        <option key={s.id} value={pick(s, "title")}>{pick(s, "title")}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className={labelCls}>{t("f_pickup")}</label>
                    <input required value={form.pickup} onChange={set("pickup")} className={inputCls} data-testid="booking-pickup" placeholder="..." />
                  </div>
                  <div>
                    <label className={labelCls}>{t("f_dropoff")}</label>
                    <input required value={form.dropoff} onChange={set("dropoff")} className={inputCls} data-testid="booking-dropoff" placeholder="..." />
                  </div>
                  <div>
                    <label className={labelCls}>{t("f_date")}</label>
                    <input type="date" value={form.date} onChange={set("date")} className={inputCls} data-testid="booking-date" />
                  </div>
                  <div>
                    <label className={labelCls}>{t("f_time")}</label>
                    <input type="time" value={form.time} onChange={set("time")} className={inputCls} data-testid="booking-time" />
                  </div>
                  <div className="md:col-span-2">
                    <label className={labelCls}>{t("f_pax")}</label>
                    <input type="number" min="1" max="20" value={form.passengers} onChange={set("passengers")} className={inputCls} data-testid="booking-passengers" />
                  </div>
                  <div className="md:col-span-2">
                    <label className={labelCls}>{t("f_notes")}</label>
                    <textarea rows="3" value={form.notes} onChange={set("notes")} className={inputCls} data-testid="booking-notes" placeholder="..." />
                  </div>
                </div>

                <motion.button whileTap={{ scale: 0.98 }} type="submit" disabled={status === "sending"}
                  className="mt-7 w-full bg-taxi text-black font-bold text-base py-4 rounded-full hover:bg-white transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
                  data-testid="booking-submit">
                  {status === "sending" ? t("f_sending") : t("f_submit")}
                  {status !== "sending" && <PaperPlaneRight size={18} weight="fill" />}
                </motion.button>

                {status === "success" && (
                  <div className="mt-4 flex items-center gap-2 text-[#25D366] font-semibold" data-testid="booking-success">
                    <CheckCircle size={20} weight="fill" /> {t("f_success")}
                  </div>
                )}
                {status === "error" && (
                  <div className="mt-4 text-usared font-semibold" data-testid="booking-error">{t("f_error")}</div>
                )}
              </form>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};
